import { message } from 'antd'
import { TrashButton } from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { AssetForm } from 'features/assetDatabase/components/assetOverview/assetDetails/AssetForm'
import { EditSaveCancelButtons } from 'features/assetDatabase/components/assetOverview/EditSaveCancelButtons'
import {
  AssetOverviewDataContext,
  HAS_MISSING_DETAILS_FIELDS,
} from 'features/assetDatabase/contexts/AssetOverviewDataLayer'
import { AssetOverviewUXContext } from 'features/assetDatabase/contexts/AssetOverviewUXLayer'
import { InfoTable } from 'features/assetDatabase/utils/sharedStyles'
import {
  AssetOverviewAdminGate,
  AssetOverviewEditorGate,
  useGeckoAuth,
} from 'features/auth'
import { useGeckoLocale } from 'features/locale'
import React, { useContext, useRef, useState } from 'react'

const InfoCard: React.FC = () => {
  const {
    inResolveMode,
    hasMissingDerivedLayerFields,
    inEditMode,
    handleSave,
    handleCancel,
    handleEdit,
    handleSaveToRolodex,
    handleDeleteInRolodex,
  } = useContext(AssetOverviewUXContext)
  const { assetDataObj, refreshData, unit, customFields } = useContext(
    AssetOverviewDataContext
  )
  const { permissions } = useGeckoAuth()
  const { measurementSystem } = useGeckoLocale()
  const formRef = useRef<HTMLFormElement>(null)
  const [messageApi] = message.useMessage()

  const currentlyEditingAsset = inEditMode.includes('asset')
  const shouldShowSaveCancelButtons =
    (currentlyEditingAsset ||
      (inResolveMode &&
        hasMissingDerivedLayerFields[HAS_MISSING_DETAILS_FIELDS])) ??
    false
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })

  const handleDeleteClick = (
    e: React.MouseEvent,
    setPopupPosition: React.Dispatch<
      React.SetStateAction<{ top: number; left: number }>
    >
  ) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + 8, // 8px below the delete icon
      left: rect.left + rect.width / 2, // Centered on delete icon
    })
    setPopoverOpen(true)
  }
  return (
    <CantileverCard>
      <CantileverCard.CardHeader
        leadingContent='Info'
        trailingContent={
          <AssetOverviewEditorGate>
            {popoverOpen && (
              <ConfirmDeletePopup
                entityName='Asset Model'
                popupPosition={popupPosition}
                cancelDelete={() => setPopoverOpen(false)}
                confirmDelete={() => {
                  if (assetDataObj?.assetModel?.id !== undefined) {
                    handleDeleteInRolodex(
                      assetDataObj?.assetModel?.id,
                      messageApi,
                      refreshData
                    )
                  } else {
                    messageApi.error('No valid asset model id provided')
                  }
                  setPopoverOpen(false)
                }}
                deleteMessage={
                  'Are you sure you want to delete this asset model? Doing so will delete all the metadata about the unit and component meta data tied to the entry.'
                }
              />
            )}
            <EditSaveCancelButtons
              saveCancelButtons={shouldShowSaveCancelButtons}
              formRef={formRef}
              handleSave={handleSave}
              handleEdit={handleEdit}
              handleCancel={handleCancel}
              formID='asset'
            />
            <AssetOverviewAdminGate>
              {assetDataObj?.assetModel && (
                <CantileverCard.HiddenUnlessHovering>
                  <TrashButton
                    onClick={(e) => handleDeleteClick(e, setPopupPosition)}
                  />
                </CantileverCard.HiddenUnlessHovering>
              )}
            </AssetOverviewAdminGate>
          </AssetOverviewEditorGate>
        }
      />

      <CantileverCard.Section>
        <InfoTable style={{ marginTop: '1rem' }}>
          {unit && assetDataObj && permissions && (
            <AssetForm
              assetDataObj={assetDataObj}
              measurementSystem={measurementSystem}
              inEditMode={currentlyEditingAsset}
              inResolveMode={inResolveMode}
              forceRefresh={refreshData}
              ref={formRef}
              userPermissions={permissions}
              handleSaveToRolodex={handleSaveToRolodex}
              unit={unit}
              customFields={customFields}
            />
          )}
        </InfoTable>
      </CantileverCard.Section>
    </CantileverCard>
  )
}

export default InfoCard
